import { useState, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { recurringMessagesApi, type RecurringMessageCreate } from '@/api/endpoints/recurringMessages'
import { useUI } from '@/stores/ui'
import { messagingApi } from '@/api/endpoints/messaging'
import { classesApi } from '@/api/endpoints/classes'

interface Props {
  open: boolean
  onClose: () => void
  onCreated?: () => void
}

export default function RecurringMessageModal({ open, onClose, onCreated }: Props) {
  const addToast = useUI((state) => state.addToast)
  const [submitting, setSubmitting] = useState(false)
  const [subject, setSubject] = useState('')
  const [body, setBody] = useState('')
  const [recipientType, setRecipientType] = useState<'all_parents' | 'all_teachers' | 'all_users' | 'class' | 'specific_users'>('all_parents')
  const [selectedClasses, setSelectedClasses] = useState<string[]>([])
  const [selectedUsers, setSelectedUsers] = useState<string[]>([])
  const [frequency, setFrequency] = useState<'daily' | 'weekly' | 'monthly'>('weekly')
  const [interval, setInterval] = useState(1)
  const [startDate, setStartDate] = useState('')
  const [startTime, setStartTime] = useState('09:00')
  const [endDate, setEndDate] = useState('')
  const [dayOfWeek, setDayOfWeek] = useState<number | null>(null)  // 0=Monday, 6=Sunday
  const [dayOfMonth, setDayOfMonth] = useState<number | null>(null)  // 1-31
  const [classes, setClasses] = useState<Array<{ id: string; name: string }>>([])
  const [searchQuery, setSearchQuery] = useState('')
  const [userResults, setUserResults] = useState<Array<{ id: string; name: string; role: string }>>([])

  useEffect(() => {
    if (!open) return
    const load = async () => {
      try {
        const classList = await classesApi.list()
        setClasses(classList)
      } catch {
        addToast({ message: 'Failed to load classes', type: 'error' })
      }
    }
    load()
  }, [open, addToast])

  useEffect(() => {
    if (recipientType === 'specific_users' && searchQuery.trim()) {
      const search = async () => {
        try {
          const results = await messagingApi.searchUsers(searchQuery)
          setUserResults(results)
        } catch {
          setUserResults([])
        }
      }
      const timer = setTimeout(search, 300)
      return () => clearTimeout(timer)
    } else {
      setUserResults([])
    }
  }, [searchQuery, recipientType])

  useEffect(() => {
    // Auto-set day of week based on start date for weekly
    if (frequency === 'weekly' && startDate && dayOfWeek === null) {
      const date = new Date(startDate)
      setDayOfWeek(date.getDay() === 0 ? 6 : date.getDay() - 1)  // Convert to 0=Mon, 6=Sun
    }
    // Auto-set day of month for monthly
    if (frequency === 'monthly' && startDate && dayOfMonth === null) {
      const date = new Date(startDate)
      setDayOfMonth(date.getDate())
    }
  }, [frequency, startDate, dayOfWeek, dayOfMonth])

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!body.trim() || !startDate) {
      addToast({ message: 'Please fill in message body and start date', type: 'error' })
      return
    }

    // Validate frequency-specific fields
    if (frequency === 'weekly' && dayOfWeek === null) {
      addToast({ message: 'Please select a day of the week', type: 'error' })
      return
    }
    if (frequency === 'monthly' && dayOfMonth === null) {
      addToast({ message: 'Please select a day of the month', type: 'error' })
      return
    }

    setSubmitting(true)
    try {
      // Combine start date and time into ISO string
      const startDateTime = new Date(`${startDate}T${startTime}`).toISOString()
      const endDateTime = endDate ? new Date(`${endDate}T23:59:59`).toISOString() : null

      // Build recipient_ids based on recipient_type
      let recipientIds: string[] | null = null
      if (recipientType === 'class' && selectedClasses.length > 0) {
        recipientIds = selectedClasses
      } else if (recipientType === 'specific_users' && selectedUsers.length > 0) {
        recipientIds = selectedUsers
      }

      const payload: RecurringMessageCreate = {
        subject: subject.trim() || null,
        body: body.trim(),
        recipient_type: recipientType,
        recipient_ids: recipientIds,
        frequency,
        interval,
        start_date: startDateTime,
        end_date: endDateTime,
        day_of_week: frequency === 'weekly' ? dayOfWeek : null,
        day_of_month: frequency === 'monthly' ? dayOfMonth : null,
        send_time: startTime,
      }

      await recurringMessagesApi.create(payload)
      addToast({ message: 'Recurring message created successfully', type: 'success' })
      
      // Reset form
      setSubject('')
      setBody('')
      setRecipientType('all_parents')
      setSelectedClasses([])
      setSelectedUsers([])
      setFrequency('weekly')
      setInterval(1)
      setStartDate('')
      setStartTime('09:00')
      setEndDate('')
      setDayOfWeek(null)
      setDayOfMonth(null)
      
      onCreated?.()
      onClose()
    } catch (error: any) {
      console.error('Failed to create recurring message:', error)
      addToast({
        message: error.response?.data?.detail || 'Failed to create recurring message',
        type: 'error',
      })
    } finally {
      setSubmitting(false)
    }
  }

  if (!open) return null

  return (
    <AnimatePresence>
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.95 }}
          className="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col m-4"
        >
          <div className="p-6 border-b border-gray-200 flex items-center justify-between bg-gradient-to-r from-yellow-50 to-white">
            <h2 className="text-2xl font-bold text-gray-900">Create Recurring Message</h2>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 text-2xl leading-none focus-ring rounded p-1"
              aria-label="Close"
            >
              ×
            </button>
          </div>

          <form onSubmit={onSubmit} className="flex-1 overflow-y-auto p-6 space-y-6">
            {/* Message Content */}
            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-gray-900 border-b border-gray-200 pb-2">Message Content</h3>
              
              <div>
                <label className="block text-sm font-semibold mb-2 text-gray-700">
                  Subject (Optional)
                </label>
                <input
                  type="text"
                  className="input-field w-full focus-ring"
                  value={subject}
                  onChange={(e) => setSubject(e.target.value)}
                  placeholder="Message subject"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold mb-2 text-gray-700">
                  Message Body <span className="text-red-500">*</span>
                </label>
                <textarea
                  className="input-field w-full focus-ring min-h-[120px]"
                  value={body}
                  onChange={(e) => setBody(e.target.value)}
                  placeholder="Enter your message..."
                  required
                />
              </div>
            </div>

            {/* Recipients */}
            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-gray-900 border-b border-gray-200 pb-2">Recipients</h3>
              
              <div>
                <label className="block text-sm font-semibold mb-2 text-gray-700">
                  Send To
                </label>
                <select
                  className="input-field w-full focus-ring"
                  value={recipientType}
                  onChange={(e) => setRecipientType(e.target.value as any)}
                >
                  <option value="all_parents">All Parents</option>
                  <option value="all_teachers">All Teachers</option>
                  <option value="all_users">All Users (Parents + Teachers)</option>
                  <option value="class">Specific Classes</option>
                  <option value="specific_users">Specific Users</option>
                </select>
              </div>

              {recipientType === 'class' && (
                <div>
                  <label className="block text-sm font-semibold mb-2 text-gray-700">
                    Select Classes
                  </label>
                  <div className="space-y-2 max-h-40 overflow-y-auto border border-gray-200 rounded-lg p-3">
                    {classes.map(cls => (
                      <label key={cls.id} className="flex items-center gap-2 cursor-pointer hover:bg-gray-50 p-2 rounded">
                        <input
                          type="checkbox"
                          checked={selectedClasses.includes(cls.id)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setSelectedClasses([...selectedClasses, cls.id])
                            } else {
                              setSelectedClasses(selectedClasses.filter(id => id !== cls.id))
                            }
                          }}
                          className="focus-ring"
                        />
                        <span className="text-sm text-gray-700">{cls.name}</span>
                      </label>
                    ))}
                  </div>
                </div>
              )}

              {recipientType === 'specific_users' && (
                <div>
                  <label className="block text-sm font-semibold mb-2 text-gray-700">
                    Search and Select Users
                  </label>
                  <input
                    type="text"
                    className="input-field w-full focus-ring mb-2"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="Type to search users..."
                  />
                  {userResults.length > 0 && (
                    <div className="space-y-2 max-h-40 overflow-y-auto border border-gray-200 rounded-lg p-3">
                      {userResults.map(user => (
                        <label key={user.id} className="flex items-center gap-2 cursor-pointer hover:bg-gray-50 p-2 rounded">
                          <input
                            type="checkbox"
                            checked={selectedUsers.includes(user.id)}
                            onChange={(e) => {
                              if (e.target.checked) {
                                setSelectedUsers([...selectedUsers, user.id])
                              } else {
                                setSelectedUsers(selectedUsers.filter(id => id !== user.id))
                              }
                            }}
                            className="focus-ring"
                          />
                          <span className="text-sm text-gray-700">{user.name} ({user.role})</span>
                        </label>
                      ))}
                    </div>
                  )}
                  {selectedUsers.length > 0 && (
                    <div className="mt-2 flex flex-wrap gap-2">
                      {selectedUsers.map(userId => {
                        const user = userResults.find(u => u.id === userId)
                        return user ? (
                          <span key={userId} className="inline-flex items-center gap-1 bg-blue-100 text-blue-700 px-2 py-1 rounded-full text-xs">
                            {user.name}
                            <button
                              type="button"
                              onClick={() => setSelectedUsers(selectedUsers.filter(id => id !== userId))}
                              className="hover:text-blue-900"
                            >
                              ×
                            </button>
                          </span>
                        ) : null
                      })}
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Schedule */}
            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-gray-900 border-b border-gray-200 pb-2">Schedule</h3>
              
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold mb-2 text-gray-700">
                    Frequency <span className="text-red-500">*</span>
                  </label>
                  <select
                    className="input-field w-full focus-ring"
                    value={frequency}
                    onChange={(e) => {
                      setFrequency(e.target.value as any)
                      setDayOfWeek(null)
                      setDayOfMonth(null)
                    }}
                    required
                  >
                    <option value="daily">Daily</option>
                    <option value="weekly">Weekly</option>
                    <option value="monthly">Monthly</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-semibold mb-2 text-gray-700">
                    Every <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="number"
                    min={1}
                    className="input-field w-full focus-ring"
                    value={interval}
                    onChange={(e) => setInterval(Math.max(1, Number(e.target.value) || 1))}
                    required
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    {interval === 1 
                      ? frequency === 'daily' ? 'day' : frequency === 'weekly' ? 'week' : 'month'
                      : frequency === 'daily' ? 'days' : frequency === 'weekly' ? 'weeks' : 'months'}
                  </p>
                </div>
              </div>

              {frequency === 'weekly' && (
                <div>
                  <label className="block text-sm font-semibold mb-2 text-gray-700">
                    Day of Week <span className="text-red-500">*</span>
                  </label>
                  <div className="grid grid-cols-7 gap-2">
                    {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map((day, idx) => (
                      <label
                        key={idx}
                        className={`flex items-center justify-center p-2 rounded-lg border-2 cursor-pointer transition-all ${
                          dayOfWeek === idx
                            ? 'border-yellow-500 bg-yellow-50 text-yellow-900 font-medium'
                            : 'border-gray-300 bg-white text-gray-700 hover:border-gray-400'
                        }`}
                      >
                        <input
                          type="radio"
                          name="dayOfWeek"
                          value={idx}
                          checked={dayOfWeek === idx}
                          onChange={() => setDayOfWeek(idx)}
                          className="sr-only"
                        />
                        <span className="text-sm">{day}</span>
                      </label>
                    ))}
                  </div>
                </div>
              )}

              {frequency === 'monthly' && (
                <div>
                  <label className="block text-sm font-semibold mb-2 text-gray-700">
                    Day of Month <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="number"
                    min={1}
                    max={31}
                    className="input-field w-full focus-ring"
                    value={dayOfMonth || ''}
                    onChange={(e) => setDayOfMonth(Math.max(1, Math.min(31, Number(e.target.value) || 1)))}
                    required
                  />
                </div>
              )}

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold mb-2 text-gray-700">
                    Start Date <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="date"
                    className="input-field w-full focus-ring"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold mb-2 text-gray-700">
                    Send Time <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="time"
                    className="input-field w-full focus-ring"
                    value={startTime}
                    onChange={(e) => setStartTime(e.target.value)}
                    required
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-semibold mb-2 text-gray-700">
                  End Date (Optional)
                </label>
                <input
                  type="date"
                  className="input-field w-full focus-ring"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  min={startDate}
                />
                <p className="text-xs text-gray-500 mt-1">Leave empty for no end date</p>
              </div>
            </div>

            {/* Actions */}
            <div className="flex items-center justify-end gap-3 pt-4 border-t border-gray-200">
              <button
                type="button"
                onClick={onClose}
                className="btn-secondary focus-ring"
                disabled={submitting}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="btn-primary focus-ring min-w-[140px]"
                disabled={submitting || !body.trim() || !startDate}
              >
                {submitting ? (
                  <span className="flex items-center gap-2">
                    <span className="animate-spin-smooth">⏳</span>
                    <span>Creating...</span>
                  </span>
                ) : (
                  '✨ Create Recurring Message'
                )}
              </button>
            </div>
          </form>
        </motion.div>
      </div>
    </AnimatePresence>
  )
}

